import { addDays, format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { lisbonDateParts } from "./businessMinutes";
import { CellMatch, ChartSeriesConfig, DateRange, KpiCellConfig } from "./types";

type StatusServiceRow = {
    status?: string | null;
    service?: number | string | null;
    count: string | number;
};

const rowMatches = (row: StatusServiceRow, match: CellMatch) => {
    const statusMatches = !match.statuses || match.statuses.includes(row.status || "");
    const serviceMatches = !match.services || match.services.includes(Number(row.service));
    return statusMatches && serviceMatches;
};

export const countWhere = (rows: Array<StatusServiceRow>, match: CellMatch) => {
    const total = rows.filter((row) => rowMatches(row, match)).reduce((sum, row) => sum + Number(row.count), 0);
    return total;
};

export const computeKpiCells = (rows: Array<StatusServiceRow>, cells: Array<KpiCellConfig>) => {
    const values: Record<string, number> = {};

    cells.filter((cell) => cell.match).forEach((cell) => {
        values[cell.key] = countWhere(rows, cell.match as CellMatch);
    });

    cells.filter((cell) => cell.sumOfCells).forEach((cell) => {
        const total = (cell.sumOfCells as Array<string>).reduce((sum, sourceKey) => sum + (values[sourceKey] || 0), 0);
        values[cell.key] = total;
    });

    return values;
};

type MonthlyRow = StatusServiceRow & {
    start_date_year: string | number;
    start_date_month: string | number;
};

export const monthlySeriesFromRows = (rows: Array<MonthlyRow>, series: Array<ChartSeriesConfig>) => {
    if (rows.length === 0) return [];

    const monthIndexes = rows.map((row) => Number(row.start_date_year) * 12 + (Number(row.start_date_month) - 1));
    const firstMonthIndex = Math.min(...monthIndexes);
    const lastMonthIndex = Math.max(...monthIndexes);

    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const year = Math.floor(monthIndex / 12);
        const month = monthIndex - year * 12;
        const monthRows = rows.filter(
            (row) => Number(row.start_date_year) === year && Number(row.start_date_month) - 1 === month
        );

        const point: Record<string, string | number> = {
            monthLabel: format(new Date(year, month, 1), "MMM yyyy", { locale: pt }),
        };

        series.filter((serie) => serie.services).forEach((serie) => {
            point[serie.key] = countWhere(monthRows, { services: serie.services });
        });
        series.filter((serie) => serie.sumOfSeries).forEach((serie) => {
            const total = (serie.sumOfSeries as Array<string>).reduce((sum, sourceKey) => sum + Number(point[sourceKey] || 0), 0);
            point[serie.key] = total;
        });

        points.push(point);
    }

    return points;
};

export const singleCount = (rows: Array<{ count: string | number }> | undefined) => {
    const total = Number(rows?.[0]?.count || 0);
    return total;
};

type GroupedCountRow = { count: string | number } & Record<string, string | number | null | undefined>;

export const monthlyCountsFromGroupedRows = (rows: Array<GroupedCountRow>, dateField: string) => {
    const countsByMonthIndex = new Map<number, number>();
    rows.forEach((row) => {
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        countsByMonthIndex.set(monthIndex, (countsByMonthIndex.get(monthIndex) || 0) + Number(row.count));
    });
    return countsByMonthIndex;
};

export const monthLabelFromIndex = (monthIndex: number) => {
    const label = format(new Date(Math.floor(monthIndex / 12), monthIndex % 12, 1), "MMM yyyy", { locale: pt });
    return label;
};

// Intervalo [gte, lt) do mês de um monthIndex (year*12 + mês-1) — para drills de células mensais.
export const monthRangeFromIndex = (monthIndex: number) => {
    const firstOfMonth = new Date(Math.floor(monthIndex / 12), monthIndex % 12, 1);
    const firstOfNextMonth = new Date(Math.floor((monthIndex + 1) / 12), (monthIndex + 1) % 12, 1);
    const range = { gte: format(firstOfMonth, "yyyy-MM-dd"), lt: format(firstOfNextMonth, "yyyy-MM-dd") };
    return range;
};

// Trimestres. Índice 0-based análogo ao monthIndex (year*4 + trimestre-1) para ficar contíguo entre anos.
const quarterOfMonth = (month: number) => {
    const quarter = Math.floor((month - 1) / 3) + 1;
    return quarter;
};

export const quarterIndexFromYearAndMonth = (year: number, month: number) => {
    const quarterIndex = year * 4 + (quarterOfMonth(month) - 1);
    return quarterIndex;
};

export const quarterLabelPt = (quarterIndex: number) => {
    const label = `T${(quarterIndex % 4) + 1} ${Math.floor(quarterIndex / 4)}`;
    return label;
};

// date_if_finish é um campo `date` (sem fuso) — basta partir a string ISO.
export const quarterIndexFromPlainDate = (isoDate: string) => {
    const year = Number(isoDate.slice(0, 4));
    const month = Number(isoDate.slice(5, 7));
    const quarterIndex = quarterIndexFromYearAndMonth(year, month);
    return quarterIndex;
};

// date_sent é `timestamptz` — converter para hora civil de Lisboa antes de extrair o trimestre.
export const quarterIndexFromTimestamp = (isoTimestamp: string) => {
    const parts = lisbonDateParts(isoTimestamp);
    const quarterIndex = quarterIndexFromYearAndMonth(parts.year, parts.month);
    return quarterIndex;
};

// Lista de trimestres (0-based) cobertos por um intervalo [gte, lt), limitada ao trimestre atual (sem futuros vazios).
// Permite que as séries por trimestre obedeçam ao filtro global "Período" em vez de uma janela fixa.
export const quarterIndexesInRange = (range: DateRange, now: Date) => {
    const startQuarterIndex = quarterIndexFromPlainDate(range.gte);
    const currentQuarterIndex = quarterIndexFromYearAndMonth(now.getFullYear(), now.getMonth() + 1);
    const lastIncludedDate = new Date(new Date(`${range.lt}T00:00:00`).getTime() - 24 * 60 * 60 * 1000);
    const rangeEndQuarterIndex = quarterIndexFromYearAndMonth(lastIncludedDate.getFullYear(), lastIncludedDate.getMonth() + 1);
    const endQuarterIndex = Math.min(currentQuarterIndex, rangeEndQuarterIndex);
    const quarterIndexes: Array<number> = [];
    for (let quarterIndex = startQuarterIndex; quarterIndex <= endQuarterIndex; quarterIndex++) quarterIndexes.push(quarterIndex);
    return quarterIndexes;
};

export const dailyCountsFromGroupedRows = (rows: Array<GroupedCountRow>, dateField: string) => {
    const countsByDayKey = new Map<string, number>();
    rows.forEach((row) => {
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        const day = Number(row[`${dateField}_day`]);
        if (!year || !month || !day) return;
        const dayKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        countsByDayKey.set(dayKey, (countsByDayKey.get(dayKey) || 0) + Number(row.count));
    });
    return countsByDayKey;
};

// Etiqueta de um dia num eixo/tooltip com o dia da semana em pt-PT: "Qua 22/07".
export const dayLabelWithWeekday = (day: Date) => {
    const weekdayAbbreviation = format(day, "EEE", { locale: pt });
    const label = `${weekdayAbbreviation.charAt(0).toUpperCase()}${weekdayAbbreviation.slice(1)} ${format(day, "dd/MM")}`;
    return label;
};

const dayLabelWithoutWeekday = (day: Date) => {
    const label = format(day, "dd/MM");
    return label;
};

export const dailyPointsFromCountMap = (
    countsByDayKey: Map<string, number>,
    range: DateRange,
    valueKey: string,
    dayLabelFormatter: (day: Date) => string = dayLabelWithoutWeekday
) => {
    const points = [];
    const lastDay = parseISO(range.lt);
    for (let day = parseISO(range.gte); day < lastDay; day = addDays(day, 1)) {
        const dayKey = format(day, "yyyy-MM-dd");
        const point: Record<string, string | number> = {
            monthLabel: dayLabelFormatter(day),
            [valueKey]: countsByDayKey.get(dayKey) || 0,
        };
        points.push(point);
    }
    return points;
};

type MonthlyCountSeries = {
    key: string;
    countsByMonthIndex: Map<number, number>;
};

export const monthlyPointsFromCountMaps = (seriesCounts: Array<MonthlyCountSeries>) => {
    const allMonthIndexes: Array<number> = [];
    seriesCounts.forEach((serie) => serie.countsByMonthIndex.forEach((_, monthIndex) => allMonthIndexes.push(monthIndex)));
    if (allMonthIndexes.length === 0) return [];

    const firstMonthIndex = Math.min(...allMonthIndexes);
    const lastMonthIndex = Math.max(...allMonthIndexes);

    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const point: Record<string, string | number> = { monthLabel: monthLabelFromIndex(monthIndex) };
        seriesCounts.forEach((serie) => {
            point[serie.key] = serie.countsByMonthIndex.get(monthIndex) || 0;
        });
        points.push(point);
    }
    return points;
};

type TreatmentFirstSessionRow = {
    treatment: number | string | null;
    min: { start_date: string | null };
};

export const treatmentsWithFirstSessionInRange = (rows: Array<TreatmentFirstSessionRow>, range: DateRange) => {
    const treatments = rows
        .filter((row) => {
            const firstSessionDate = row.min?.start_date;
            if (!firstSessionDate || !row.treatment) return false;
            return firstSessionDate >= range.gte && firstSessionDate < range.lt;
        })
        .map((row) => ({ treatmentId: row.treatment as number | string, firstSessionDate: row.min.start_date as string }));
    return treatments;
};

export const treatmentIdsWithFirstSessionInRange = (rows: Array<TreatmentFirstSessionRow>, range: DateRange) => {
    const treatmentIds = treatmentsWithFirstSessionInRange(rows, range).map((treatment) => treatment.treatmentId);
    return treatmentIds;
};

